import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { validatePassword } from "@/utils/passwordValidation";

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

const resetSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters.")
      .refine((val) => validatePassword(val).isValid, {
        message: "Password must include uppercase, lowercase, number, and special character.",
      }),
    confirmPassword: z.string().min(1, "Please confirm your password."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

type ResetFormValues = z.infer<typeof resetSchema>;

type LinkStatus = "checking" | "ready" | "invalid";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<LinkStatus>("checking");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Reset Password | AnySkill";
    const descContent = "Reset your AnySkill password securely.";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", descContent);

    // Keep reset flows out of search engines
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = window.location.origin + "/reset-password";
  }, []);

  useEffect(() => {
    const processLink = async () => {
      setStatus("checking");
      setLinkError(null);

      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");

        // Support legacy implicit links that return tokens in the hash
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          if (error) throw error;

          // Remove code from the URL so refresh doesn't re-trigger the exchange
          url.searchParams.delete("code");
          url.searchParams.delete("type");
          window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
        } else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;

          // Remove token hash from the URL
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          setStatus("invalid");
          setLinkError("This password reset link is invalid or has expired.");
          return;
        }

        setStatus("ready");
      } catch (e: any) {
        setStatus("invalid");
        setLinkError(e?.message || "Unable to verify password reset link.");
      }
    };

    processLink();
  }, []);

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: ResetFormValues) => {
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: values.password });
      if (error) throw error;

      toast.success("Password updated successfully");
      navigate("/my-profile");
    } catch (e: any) {
      toast.error(e?.message || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-[70vh] bg-background text-foreground">
      <section className="container mx-auto px-4 py-10">
        <div className="mx-auto max-w-lg">
          <Card>
            <CardHeader>
              <CardTitle>
                <h1 className="text-xl font-semibold">Reset your password</h1>
              </CardTitle>
              <CardDescription>
                Choose a new password for your AnySkill account.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {status === "checking" && (
                <div className="text-sm text-muted-foreground">
                  Verifying your reset link…
                </div>
              )}

              {status === "invalid" && (
                <Alert variant="destructive">
                  <AlertTitle>Reset link not valid</AlertTitle>
                  <AlertDescription>
                    {linkError || "Please request a new password reset email and try again."}
                    <div className="mt-3">
                      <Link className="underline underline-offset-4" to="/auth?mode=login">
                        Back to sign in
                      </Link>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {status === "ready" && (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New password</FormLabel>
                          <FormControl>
                            <PasswordInput autoComplete="new-password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm new password</FormLabel>
                          <FormControl>
                            <PasswordInput autoComplete="new-password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="text-sm">
                      <p className="font-medium mb-1">Password must include:</p>
                      <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                        <li>At least 8 characters</li>
                        <li>At least one uppercase letter (A-Z)</li>
                        <li>At least one lowercase letter (a-z)</li>
                        <li>At least one number (0-9)</li>
                        <li>At least one special character (!@#$%^&*)</li>
                      </ul>
                    </div>

                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? "Updating…" : "Update password"}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>

            <CardFooter className="flex items-center justify-between text-sm">
              <Link className="underline underline-offset-4" to="/auth?mode=login">
                Sign in
              </Link>
              <span className="text-muted-foreground">Need a new link? Send another reset email.</span>
            </CardFooter>
          </Card>
        </div>
      </section>
    </main>
  );
}
